import type {
  IHotelRoomCategoryAvailability,
  IHotelRoomOccupancyAvailabilityPrice,
} from "../features/hotel/hotelInterface";

type CurrencyAmount = Record<string, number>;

const toCurrencyMap = (
  amount: unknown,
  currencyCode: string,
): CurrencyAmount => {
  if (!amount || typeof amount !== "object") {
    return { [currencyCode]: 0 };
  }

  const record = amount as Record<string, unknown>;

  if (typeof record.GrossValue === "number") {
    const code =
      typeof record.Currency === "string" ? record.Currency : currencyCode;
    return { [code]: record.GrossValue };
  }

  const mapped: CurrencyAmount = {};
  for (const [code, value] of Object.entries(record)) {
    if (typeof value === "number") {
      mapped[code] = value;
    } else if (
      value &&
      typeof value === "object" &&
      typeof (value as { GrossValue?: unknown }).GrossValue === "number"
    ) {
      mapped[code] = (value as { GrossValue: number }).GrossValue;
    }
  }

  return Object.keys(mapped).length ? mapped : { [currencyCode]: 0 };
};

export const normalizeRoomPrice = (
  price: unknown,
  currencyCode: string,
): IHotelRoomOccupancyAvailabilityPrice => {
  const source = (price ?? {}) as Record<string, unknown>;
  const average =
    source.AveragePerNight ??
    source.AverageAmountPerNight ??
    source.AveragePerTimeUnit ??
    source.AverageAmountPerTimeUnit;
  const total = source.Total ?? source.TotalAmount;

  return {
    AveragePerNight: toCurrencyMap(average, currencyCode),
    Total: toCurrencyMap(total, currencyCode),
  };
};

export const normalizePricing = (
  pricing: { RateId: string; Price: unknown; MaxPrice: unknown },
  currencyCode: string,
) => ({
  RateId: pricing.RateId,
  Price: normalizeRoomPrice(pricing.Price, currencyCode),
  MaxPrice: normalizeRoomPrice(pricing.MaxPrice, currencyCode),
});

/**
 * Add pricing of rates missing from the voucher availability (the public rates)
 */
export const mergePublicPricing = (
  availabilities: IHotelRoomCategoryAvailability[],
  publicAvailabilities: IHotelRoomCategoryAvailability[],
): IHotelRoomCategoryAvailability[] =>
  availabilities.map((rca) => {
    const publicRca = publicAvailabilities.find(
      (p) => p.RoomCategoryId === rca.RoomCategoryId,
    );
    if (!publicRca) return rca;

    return {
      ...rca,
      RoomOccupancyAvailabilities: rca.RoomOccupancyAvailabilities.map((roa) => {
        const publicRoa = publicRca.RoomOccupancyAvailabilities.find(
          (p) => p.AdultCount === roa.AdultCount && p.ChildCount === roa.ChildCount,
        );
        if (!publicRoa) return roa;

        const missing = publicRoa.Pricing.filter(
          (p) => !roa.Pricing.some((v) => v.RateId === p.RateId),
        );
        return { ...roa, Pricing: [...roa.Pricing, ...missing] };
      }),
    };
  });

export const normalizeRoomCategoryAvailabilities = (
  availabilities: IHotelRoomCategoryAvailability[] | undefined,
  currencyCode: string,
): IHotelRoomCategoryAvailability[] => {
  if (!availabilities) return [];

  return availabilities.map((category) => ({
    ...category,
    RoomOccupancyAvailabilities: (
      category.RoomOccupancyAvailabilities ?? []
    ).map((occupancy) => {
      const occupancyData = Array.isArray(occupancy.OccupancyData)
        ? occupancy.OccupancyData
        : [];
      const personCount = occupancyData.reduce(
        (sum, item) => sum + (item.PersonCount ?? 0),
        0,
      );

      return {
        ...occupancy,
        AdultCount:
          typeof occupancy.AdultCount === "number"
            ? occupancy.AdultCount
            : personCount || 1,
        ChildCount:
          typeof occupancy.ChildCount === "number" ? occupancy.ChildCount : 0,
        OccupancyData: occupancyData,
        Pricing: (occupancy.Pricing ?? []).map((pricing) => ({
          RateId: pricing.RateId,
          Price: normalizeRoomPrice(pricing.Price, currencyCode),
          MaxPrice: normalizeRoomPrice(pricing.MaxPrice, currencyCode),
        })),
      };
    }),
  }));
};
